import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchColumns, fetchDatabases, fetchSchemas, fetchTables } from '@/api/endpoints';
import { formatCount } from '@/lib/format';
import { ErrorState, LoadingState } from '@/components/common/states';
import { asQueryError } from '@/query/execute';
import type { CatalogColumn, CatalogTable } from '@/types/api';

export interface ObjectExplorerProps {
  /** Inserts a generated SELECT into the active cell. */
  readonly onInsertQuery: (sql: string) => void;
  readonly onUpload: () => void;
}

interface TreeState {
  readonly databases: ReadonlySet<string>;
  readonly schemas: ReadonlySet<string>;
  readonly tables: ReadonlySet<string>;
}

function toggle(set: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

export function ObjectExplorer({ onInsertQuery, onUpload }: ObjectExplorerProps): JSX.Element {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<TreeState>({
    databases: new Set(['lake']),
    schemas: new Set(['lake.raqeem']),
    tables: new Set<string>(),
  });

  const databases = useQuery({ queryKey: ['catalog', 'databases'], queryFn: fetchDatabases });
  const schemas = useQuery({ queryKey: ['catalog', 'schemas'], queryFn: () => fetchSchemas() });
  const tables = useQuery({ queryKey: ['catalog', 'tables'], queryFn: () => fetchTables() });
  const columns = useQuery({ queryKey: ['catalog', 'columns'], queryFn: () => fetchColumns() });

  const needle = search.trim().toLowerCase();

  const matchingTables = useMemo(() => {
    const all = tables.data ?? [];
    if (!needle) return all;
    const columnHits = new Set(
      (columns.data ?? [])
        .filter((column) => column.name.toLowerCase().includes(needle))
        .map((column) => `${column.database}.${column.schema}.${column.table}`),
    );
    return all.filter(
      (table) =>
        table.name.toLowerCase().includes(needle) ||
        table.schema.toLowerCase().includes(needle) ||
        columnHits.has(`${table.database}.${table.schema}.${table.name}`),
    );
  }, [tables.data, columns.data, needle]);

  const columnsByTable = useMemo(() => {
    const map = new Map<string, CatalogColumn[]>();
    for (const column of columns.data ?? []) {
      const key = `${column.database}.${column.schema}.${column.table}`;
      const list = map.get(key);
      if (list) list.push(column);
      else map.set(key, [column]);
    }
    return map;
  }, [columns.data]);

  const selectFor = (table: CatalogTable): string => {
    const cols = columnsByTable.get(`${table.database}.${table.schema}.${table.name}`) ?? [];
    const projection =
      cols.length > 0 && cols.length <= 12
        ? cols.map((column) => `  "${column.name}"`).join(',\n')
        : '  *';
    return `SELECT\n${projection}\nFROM ${table.schema}.${table.name}\nLIMIT 500;`;
  };

  const error = databases.error ?? schemas.error ?? tables.error;

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="object-explorer">
      <div className="panel-head">
        <span className="eyebrow">Objects</span>
        <button type="button" className="icon-btn" onClick={onUpload} title="Upload a .parquet file">
          + Parquet
        </button>
      </div>

      <div className="border-b border-line p-1.5">
        <input
          className="field"
          type="search"
          placeholder="Search tables and columns"
          aria-label="Search catalog"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-auto py-1" role="tree" aria-label="Database catalog">
        {databases.isLoading ? <LoadingState label="Loading catalog" /> : null}

        {error ? (
          <ErrorState error={asQueryError(error)} onRetry={() => void databases.refetch()} />
        ) : null}

        {(databases.data ?? []).map((database) => {
          const dbOpen = expanded.databases.has(database.name) || needle !== '';
          const dbSchemas = (schemas.data ?? []).filter(
            (schema) => schema.database === database.name,
          );

          return (
            <div key={database.name} role="treeitem" aria-expanded={dbOpen}>
              <button
                type="button"
                className="tree-row"
                style={{ paddingLeft: 6 }}
                onClick={() =>
                  setExpanded((state) => ({
                    ...state,
                    databases: toggle(state.databases, database.name),
                  }))
                }
                title={database.comment ?? database.name}
              >
                <Chevron open={dbOpen} />
                <span className="text-faint" aria-hidden="true">
                  ▤
                </span>
                <span className="truncate font-medium">{database.name}</span>
              </button>

              {dbOpen
                ? dbSchemas.map((schema) => {
                    const schemaKey = `${database.name}.${schema.name}`;
                    const schemaTables = matchingTables.filter(
                      (table) => table.database === database.name && table.schema === schema.name,
                    );
                    if (needle && schemaTables.length === 0) return null;
                    const schemaOpen = expanded.schemas.has(schemaKey) || needle !== '';

                    return (
                      <div key={schemaKey} role="treeitem" aria-expanded={schemaOpen}>
                        <button
                          type="button"
                          className="tree-row"
                          style={{ paddingLeft: 18 }}
                          onClick={() =>
                            setExpanded((state) => ({
                              ...state,
                              schemas: toggle(state.schemas, schemaKey),
                            }))
                          }
                        >
                          <Chevron open={schemaOpen} />
                          <span className="truncate text-muted">{schema.name}</span>
                          <span className="tnum ml-auto shrink-0 text-[10px] text-faint">
                            {schemaTables.length}
                          </span>
                        </button>

                        {schemaOpen
                          ? schemaTables.map((table) => {
                              const tableKey = `${database.name}.${schema.name}.${table.name}`;
                              const tableOpen = expanded.tables.has(tableKey);
                              const tableColumns = columnsByTable.get(tableKey) ?? [];

                              return (
                                <div key={tableKey} role="treeitem" aria-expanded={tableOpen}>
                                  <div className="group flex">
                                    <button
                                      type="button"
                                      className="tree-row"
                                      style={{ paddingLeft: 30 }}
                                      onClick={() =>
                                        setExpanded((state) => ({
                                          ...state,
                                          tables: toggle(state.tables, tableKey),
                                        }))
                                      }
                                      onDoubleClick={() => onInsertQuery(selectFor(table))}
                                      title={table.comment ?? `${table.kind} ${tableKey}`}
                                    >
                                      <Chevron open={tableOpen} />
                                      <span
                                        className="shrink-0 text-[10px] text-faint"
                                        aria-hidden="true"
                                      >
                                        {table.kind === 'view' ? '◇' : '▦'}
                                      </span>
                                      <span className="truncate font-mono text-[11px]">
                                        {table.name}
                                      </span>
                                      {typeof table.rowCount === 'number' ? (
                                        <span className="tnum ml-auto shrink-0 pl-2 text-[10px] text-faint">
                                          {formatCount(table.rowCount)}
                                        </span>
                                      ) : null}
                                    </button>
                                    <button
                                      type="button"
                                      className="icon-btn mr-1 opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
                                      aria-label={`Insert SELECT for ${table.name}`}
                                      onClick={() => onInsertQuery(selectFor(table))}
                                    >
                                      ↵
                                    </button>
                                  </div>

                                  {tableOpen
                                    ? tableColumns.map((column) => (
                                        <button
                                          key={column.name}
                                          type="button"
                                          className="tree-row"
                                          style={{ paddingLeft: 48 }}
                                          onClick={() => onInsertQuery(`"${column.name}"`)}
                                          title={`${column.typeText}${column.isPartitionKey ? ' · partition key' : ''}`}
                                        >
                                          <span className="truncate font-mono text-[11px] text-muted">
                                            {column.name}
                                          </span>
                                          <span className="ml-auto shrink-0 pl-2 text-[10px] text-faint">
                                            {column.isPartitionKey ? '⊞ ' : ''}
                                            {column.typeText.toLowerCase()}
                                          </span>
                                        </button>
                                      ))
                                    : null}
                                </div>
                              );
                            })
                          : null}
                      </div>
                    );
                  })
                : null}
            </div>
          );
        })}

        {needle && matchingTables.length === 0 ? (
          <p className="p-4 text-center text-xs text-muted">No object matches “{search}”.</p>
        ) : null}
      </div>
    </div>
  );
}

function Chevron({ open }: { open: boolean }): JSX.Element {
  return (
    <span className="w-2.5 shrink-0 text-[9px] text-faint" aria-hidden="true">
      {open ? '▾' : '▸'}
    </span>
  );
}
